import { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wallet, MagnifyingGlass, Warning, CheckCircle, Buildings, ListBullets, Briefcase, UserCircle, CaretRight, CaretDown, FileText } from "@phosphor-icons/react";

const fmtBRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const fmtBRLfull = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 });

const STATUS_FILTERS = [
  { v: "", l: "Todos os saldos" },
  { v: "critico", l: "Crítico (≥ 80% consumido)" },
  { v: "alerta", l: "Alerta (60-79% consumido)" },
  { v: "ok", l: "OK (< 60% consumido)" },
];

const colorByPct = (pct) => {
  if (pct >= 100) return { bg: "#EF444420", text: "#EF4444", bar: "#EF4444", label: "ESGOTADO" };
  if (pct >= 80)  return { bg: "#EF444420", text: "#EF4444", bar: "#EF4444", label: "CRÍTICO" };
  if (pct >= 60)  return { bg: "#F59E0B20", text: "#F59E0B", bar: "#F59E0B", label: "ALERTA" };
  return { bg: "#10B98120", text: "#10B981", bar: "#10B981", label: "OK" };
};

export default function Saldos() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [munis, setMunis] = useState([]);
  const [search, setSearch] = useState("");
  const [statusSaldo, setStatusSaldo] = useState("");
  const [filtroMun, setFiltroMun] = useState("");
  const [view, setView] = useState("contratos"); // contratos | secretaria | fiscal

  useEffect(() => { api.get("/municipalities").then((r) => setMunis(r.data)); }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = {};
      if (search) params.search = search;
      if (statusSaldo) params.status_saldo = statusSaldo;
      if (filtroMun) params.municipio_id = filtroMun;
      api.get("/saldos", { params }).then((r) => setData(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [search, statusSaldo, filtroMun]);

  if (!data) return <div className="text-[#94A3B8]">Carregando saldos…</div>;

  return (
    <div className="space-y-6">
      <div>
        <div className="uppercase-label">Controle financeiro</div>
        <h1 className="font-heading text-4xl tracking-tight mt-2">Controle de Saldos Contratuais</h1>
        <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl">
          Acompanhe o saldo consolidado dos contratos do seu município, com indicadores de
          alerta para contratos próximos do esgotamento financeiro.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <Kpi label="Contratos analisados" value={data.total_contratos.toLocaleString("pt-BR")} icon={Wallet} />
        <Kpi label="Valor consolidado" value={fmtBRL(data.valor_total)} icon={Wallet} accent />
        <Kpi label="Valor executado" value={fmtBRL(data.valor_executado_total)} color="#F59E0B" />
        <Kpi label="Saldo disponível" value={fmtBRL(data.saldo_total)}
             color={data.saldo_total < 0 ? "#EF4444" : "#10B981"} />
      </div>

      {/* Filtros */}
      <div className="tce-card p-5">
        <div className={`grid grid-cols-1 md:grid-cols-${isAdmin ? 3 : 2} gap-3`}>
          <div className="md:col-span-1 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input data-testid="saldos-search" value={search}
                   onChange={(e) => setSearch(e.target.value)}
                   placeholder="Buscar contrato, objeto, fornecedor…"
                   className="tce-input w-full pl-10 pr-4 py-2.5 rounded-md text-sm" />
          </div>
          <select data-testid="saldos-status" value={statusSaldo}
                  onChange={(e) => setStatusSaldo(e.target.value)}
                  className="tce-input px-3 py-2.5 rounded-md text-sm">
            {STATUS_FILTERS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          {isAdmin && (
            <select data-testid="saldos-muni" value={filtroMun} onChange={(e) => setFiltroMun(e.target.value)}
                    className="tce-input px-3 py-2.5 rounded-md text-sm">
              <option value="">Todos os municípios</option>
              {munis.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Abas de visualização */}
      <div className="flex items-center gap-2 border-b border-[#334155]">
        <TabBtn active={view === "contratos"} onClick={() => setView("contratos")}
                icon={ListBullets} label={`Por Contrato (${data.items.length})`} testid="tab-contratos" />
        <TabBtn active={view === "secretaria"} onClick={() => setView("secretaria")}
                icon={Briefcase} label={`Por Secretaria / Processo (${data.por_secretaria?.length || 0})`} testid="tab-secretaria" />
        <TabBtn active={view === "fiscal"} onClick={() => setView("fiscal")}
                icon={UserCircle} label={`Por Fornecedor (${data.por_fiscal?.length || 0})`} testid="tab-fiscal" />
      </div>

      {view === "contratos" && (
      /* Tabela */
      <div className="tce-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#1E293B]">
              <tr className="text-left">
                <th className="px-5 py-4 uppercase-label">Contrato</th>
                <th className="px-5 py-4 uppercase-label">Município</th>
                <th className="px-5 py-4 uppercase-label text-right">Total</th>
                <th className="px-5 py-4 uppercase-label text-right">Executado</th>
                <th className="px-5 py-4 uppercase-label text-right">Saldo</th>
                <th className="px-5 py-4 uppercase-label">Consumo</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-[#94A3B8]">Nenhum contrato encontrado.</td></tr>
              ) : data.items.map((c, i) => {
                const col = colorByPct(c.pct_consumido);
                return (
                  <tr key={c.id} data-testid={`saldo-row-${i}`} className="border-t border-[#334155] hover:bg-[#1E293B]/60">
                    <td className="px-5 py-4">
                      <Link to={`/contratos/${c.id}`} className="font-mono text-[#F59E0B] hover:text-[#FCD34D]">{c.numero}</Link>
                      <div className="text-xs text-[#94A3B8] mt-0.5 max-w-[260px] truncate">{c.objeto}</div>
                    </td>
                    <td className="px-5 py-4 text-[#94A3B8]">
                      <span className="flex items-center gap-1.5">
                        <Buildings size={12} weight="duotone" className="text-[#F59E0B]" />
                        {c.municipio_nome}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right font-mono text-xs">{fmtBRLfull(c.valor)}</td>
                    <td className="px-5 py-4 text-right font-mono text-xs text-[#F59E0B]">{fmtBRLfull(c.valor_executado)}</td>
                    <td className={`px-5 py-4 text-right font-mono text-xs font-bold`}
                        style={{ color: c.saldo < 0 ? "#EF4444" : "#10B981" }}>
                      {fmtBRLfull(c.saldo)}
                    </td>
                    <td className="px-5 py-4 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex-1 h-2 bg-[#1E293B] rounded-full overflow-hidden">
                          <div className="h-full transition-all"
                               style={{ width: `${Math.min(c.pct_consumido, 100)}%`, background: col.bar }} />
                        </div>
                        <span className="text-xs font-mono font-bold w-14 text-right" style={{ color: col.text }}>
                          {c.pct_consumido.toFixed(1)}%
                        </span>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: col.text }}>
                        {col.label === "OK" ? <><CheckCircle size={10} className="inline mr-1" weight="fill" />OK</> :
                          <><Warning size={10} className="inline mr-1" weight="fill" />{col.label}</>}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {view === "secretaria" && <SecretariaProcessoTable items={data.por_secretaria} />}
      {view === "fiscal" && <GroupTable items={data.por_fiscal} labelCol="Fornecedor" testid="fis" />}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
            className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${
              active ? "border-[#F59E0B] text-[#F59E0B]" : "border-transparent text-[#94A3B8] hover:text-[#E9EEF5]"
            }`}>
      <Icon size={16} weight={active ? "duotone" : "regular"} /> {label}
    </button>
  );
}

function GroupTable({ items, labelCol, testid }) {
  if (!items || items.length === 0) {
    return <div className="tce-card p-10 text-center text-[#94A3B8]">Nenhum agrupamento disponível.</div>;
  }
  return (
    <div className="tce-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1E293B]">
            <tr className="text-left">
              <th className="px-5 py-4 uppercase-label">{labelCol}</th>
              <th className="px-5 py-4 uppercase-label text-right">Contratos</th>
              <th className="px-5 py-4 uppercase-label text-right">Valor total</th>
              <th className="px-5 py-4 uppercase-label text-right">Executado</th>
              <th className="px-5 py-4 uppercase-label text-right">Saldo</th>
              <th className="px-5 py-4 uppercase-label">Consumo</th>
            </tr>
          </thead>
          <tbody>
            {items.map((g, i) => {
              const col = colorByPct(g.pct_consumido);
              return (
                <tr key={g.nome} data-testid={`${testid}-row-${i}`}
                    className="border-t border-[#334155] hover:bg-[#1E293B]/60">
                  <td className="px-5 py-4 text-[#E9EEF5] max-w-[420px] truncate" title={g.nome}>{g.nome}</td>
                  <td className="px-5 py-4 text-right font-mono">{g.contratos}</td>
                  <td className="px-5 py-4 text-right font-mono text-xs">{fmtBRLfull(g.valor)}</td>
                  <td className="px-5 py-4 text-right font-mono text-xs text-[#F59E0B]">{fmtBRLfull(g.executado)}</td>
                  <td className="px-5 py-4 text-right font-mono text-xs font-bold"
                      style={{ color: g.saldo < 0 ? "#EF4444" : "#10B981" }}>
                    {fmtBRLfull(g.saldo)}
                  </td>
                  <td className="px-5 py-4 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-[#1E293B] rounded-full overflow-hidden">
                        <div className="h-full transition-all"
                             style={{ width: `${Math.min(g.pct_consumido, 100)}%`, background: col.bar }} />
                      </div>
                      <span className="text-xs font-mono font-bold w-14 text-right" style={{ color: col.text }}>
                        {g.pct_consumido.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SecretariaProcessoTable({ items }) {
  const [expanded, setExpanded] = useState({});
  if (!items || items.length === 0) {
    return <div className="tce-card p-10 text-center text-[#94A3B8]">Nenhum agrupamento disponível.</div>;
  }
  const toggle = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }));
  return (
    <div className="tce-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1E293B]">
            <tr className="text-left">
              <th className="px-5 py-4 uppercase-label">Secretaria / Processo</th>
              <th className="px-5 py-4 uppercase-label text-right">Contratos</th>
              <th className="px-5 py-4 uppercase-label text-right">Valor total</th>
              <th className="px-5 py-4 uppercase-label text-right">Executado</th>
              <th className="px-5 py-4 uppercase-label text-right">Saldo</th>
              <th className="px-5 py-4 uppercase-label">Consumo</th>
            </tr>
          </thead>
          <tbody>
            {items.map((sec, si) => {
              const isOpen = !!expanded[sec.nome];
              const col = colorByPct(sec.pct_consumido);
              const Caret = isOpen ? CaretDown : CaretRight;
              return (
                <Fragment key={`sec-${si}`}>
                  <tr data-testid={`sec-row-${si}`}
                      className="border-t border-[#334155] hover:bg-[#1E293B]/60 cursor-pointer"
                      onClick={() => toggle(sec.nome)}>
                    <td className="px-5 py-4">
                      <span className="flex items-center gap-2">
                        <Caret size={14} weight="bold" className="text-[#F59E0B]" />
                        <Briefcase size={14} weight="duotone" className="text-[#F59E0B]" />
                        <span className="font-semibold text-[#E9EEF5] max-w-[420px] truncate" title={sec.nome}>{sec.nome}</span>
                        <span className="text-xs text-[#94A3B8] ml-1">({sec.processos?.length || 0} processos)</span>
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right font-mono">{sec.contratos}</td>
                    <td className="px-5 py-4 text-right font-mono text-xs">{fmtBRLfull(sec.valor)}</td>
                    <td className="px-5 py-4 text-right font-mono text-xs text-[#F59E0B]">{fmtBRLfull(sec.executado)}</td>
                    <td className="px-5 py-4 text-right font-mono text-xs font-bold"
                        style={{ color: sec.saldo < 0 ? "#EF4444" : "#10B981" }}>{fmtBRLfull(sec.saldo)}</td>
                    <td className="px-5 py-4 min-w-[180px]">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-[#1E293B] rounded-full overflow-hidden">
                          <div className="h-full transition-all"
                               style={{ width: `${Math.min(sec.pct_consumido, 100)}%`, background: col.bar }} />
                        </div>
                        <span className="text-xs font-mono font-bold w-14 text-right" style={{ color: col.text }}>
                          {sec.pct_consumido.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (sec.processos || []).map((p, pi) => {
                    const pc = colorByPct(p.pct_consumido);
                    return (
                      <tr key={`sec-${si}-p-${pi}`} data-testid={`sec-${si}-proc-${pi}`}
                          className="border-t border-[#334155]/50 bg-[#0E141C]/40">
                        <td className="px-5 py-3 pl-14">
                          <span className="flex items-center gap-2">
                            <FileText size={12} weight="duotone" className="text-[#94A3B8]" />
                            <span className="text-[#E9EEF5] text-xs max-w-[380px] truncate" title={p.nome}>{p.nome}</span>
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right font-mono text-xs">{p.contratos}</td>
                        <td className="px-5 py-3 text-right font-mono text-xs">{fmtBRLfull(p.valor)}</td>
                        <td className="px-5 py-3 text-right font-mono text-xs text-[#F59E0B]">{fmtBRLfull(p.executado)}</td>
                        <td className="px-5 py-3 text-right font-mono text-xs font-bold"
                            style={{ color: p.saldo < 0 ? "#EF4444" : "#10B981" }}>{fmtBRLfull(p.saldo)}</td>
                        <td className="px-5 py-3 min-w-[180px]">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-[#1E293B] rounded-full overflow-hidden">
                              <div className="h-full transition-all"
                                   style={{ width: `${Math.min(p.pct_consumido, 100)}%`, background: pc.bar }} />
                            </div>
                            <span className="text-[10px] font-mono font-bold w-12 text-right" style={{ color: pc.text }}>
                              {p.pct_consumido.toFixed(1)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent, color }) {
  return (
    <div className="tce-card p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="uppercase-label">{label}</div>
          <div className={`font-heading text-2xl mt-2 tracking-tight ${accent ? "gold-text" : ""}`}
               style={!accent && color ? { color } : undefined}>
            {value}
          </div>
        </div>
        {Icon && (
          <div className="w-10 h-10 rounded-md bg-[#1E293B] flex items-center justify-center text-[#F59E0B]">
            <Icon size={18} weight="duotone" />
          </div>
        )}
      </div>
    </div>
  );
}
